// Shell layout: top nav with sign-in / sign-out, content slot, footer.

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.js';
import { LanguagePicker } from '../i18n/LanguagePicker.js';
import { PRIVACY_POLICY_URL, TERMS_URL } from '../lib/legalLinks.js';
import { EnvironmentBadge } from './EnvironmentBadge.js';
import { FeedbackLauncher } from './feedback/FeedbackLauncher.js';
import { ImpersonationBanner } from './ImpersonationBanner.js';
import { StartNewOrderButton } from './StartNewOrderButton.js';

// Full-bleed auth surfaces render their own centered branding (the Argo
// lockup in the page body), so the app chrome is suppressed on them to
// match the Nexus ERP sign-in experience.
const CHROMELESS_ROUTES = new Set(['/login']);

export function Layout({ children }: { children: ReactNode }): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const chromeless = CHROMELESS_ROUTES.has(location.pathname);
  const { t } = useTranslation('common');
  const impersonating = auth.status === 'signed_in' && Boolean(auth.impersonation);

  return (
    <div className={impersonating ? 'app-shell app-shell--feedback-launcher' : 'app-shell'}>
      <ImpersonationBanner />
      {!chromeless && (
        <header className="app-header">
          <div className="app-header__brand">
            <Link to="/">{t('app.name')}</Link>
            <EnvironmentBadge />
          </div>
          <nav className="app-header__nav">
            {auth.status === 'signed_in' && (
              <>
                {/* Secondary nav: view-only list of past + in-progress orders. */}
                <Link to="/quotes">{t('nav.myOrders')}</Link>
                {/* The single primary action for starting a new order. */}
                <StartNewOrderButton />
                <span className="app-header__email">{auth.user.email}</span>
                <button
                  type="button"
                  onClick={() => {
                    void (async () => {
                      await auth.logout();
                      navigate('/login', { replace: true });
                    })();
                  }}
                >
                  {t('nav.signOut')}
                </button>
              </>
            )}
            {auth.status === 'signed_out' && (
              <>
                <Link to="/login">{t('nav.signIn')}</Link>
                <Link to="/register">{t('nav.createAccount')}</Link>
              </>
            )}
          </nav>
        </header>
      )}
      <main className={chromeless ? 'app-main app-main--auth' : 'app-main'}>{children}</main>
      <footer className="app-footer">
        <small>{t('footer.copyright')}</small>
        <nav className="app-footer__links" aria-label={t('footer.legalLabel')}>
          <a
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('footer.externalAria', { document: t('footer.privacy') })}
          >
            {t('footer.privacy')}
          </a>
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('footer.externalAria', { document: t('footer.terms') })}
          >
            {t('footer.terms')}
          </a>
        </nav>
        <LanguagePicker />
      </footer>
      <FeedbackLauncher />
    </div>
  );
}
